import WbHttpClient from '../../../services/wbhttpclient';

// Type definitions
export interface FinancialEntity {
  id: number;
  status: number;
  cadence: number;
  dataProviderUserId: number;
  currencyId: number;
  createAt: number;
  createdBy: number;
  lastUpdate: number;
}

export interface FinancialCategory {
  id: number;
  type: number;
  isEnabled: boolean;
  initialValue: number;
  currentValue: number;
}

export interface FinancialMapping {
  objectType: number;
  objectId: number;
}

export interface CreateFinancialEntityParams {
  objectType: number; // 1 for OKR
  objectId: number;
  cadence: number; // 3 for Monthly (or 2 for Weekly, then weeklyUpdateDay required)
  weeklyUpdateDay?: number; // optional, required if cadence === 2
  dataProviderUserIds: number[];
  currencyId: number;
  fields: Array<{
    type: number;
    isEnabled: boolean;
    value?: number; // optional value for Budget, etc.
  }>;
}

export interface FinancialEntityResponse {
  financial: FinancialEntity | null;
  mapping: FinancialMapping;
  categories: FinancialCategory[];
  userResources: unknown[];
  teamResources: unknown[];
}

export type FinancialsConfigureState = {
  currency?: number;
  cadence?: {
    id: number;
    weekDay?: number;
  };
  dataProviders?: Array<{
    id: number;
    name: string;
    email?: string;
    picture?: string;
  }>;
  budget?: number;
  resources?: number;
  runningCost?: number;
}

export const FinancialCadence = {
  Daily: 1,
  Weekly: 2,
  Monthly: 3,
  Quarterly: 4,
}

export const FinancialType = {
  Budget: 1,
  Resources: 2,
  Cost: 3,
}

export class FinancialsService {
  private WbHttpClient: WbHttpClient;

  constructor() {
    this.WbHttpClient = new WbHttpClient();
  }

  async getEntityByObject(params: { objectType: number; objectId: number }) {
    return this.WbHttpClient.get('vnext/financials-api/financial/entity/by-object', params)?.catch(
      (error) => null,
    ) as Promise<{ data: FinancialEntityResponse | null }>;
  }

  async createEntity(objectType: number, objectId: number, formState: FinancialsConfigureState) {
    const payload = {
      objectType,
      objectId,
      cadence: formState.cadence?.id,
      ...(formState.cadence?.id === FinancialCadence.Weekly && {
        weeklyUpdateDay: formState.cadence?.weekDay ?? 1,
      }),
      dataProviderUserIds: formState.dataProviders.map((p) => p.id),
      currencyId: formState.currency,
      fields: [
        {
          type: FinancialType.Budget,
          isEnabled: true,
          value: formState.budget,
        },
        {
          type: FinancialType.Resources,
          isEnabled: true,
          value: formState.resources,
        },
        {
          type: FinancialType.Cost,
          isEnabled: true,
          value: formState.runningCost,
        },
      ],
    } satisfies CreateFinancialEntityParams;
    return this.WbHttpClient.postJson(
      'vnext/financials-api/financial/entity',
      payload as unknown as Record<string, unknown>,
    )?.catch(
      (error) => null,
    ) as Promise<{ data: FinancialEntityResponse | null }>;
  }

  async resyncIntegration(id: number) {
    return this.WbHttpClient.get(
      `vnext/financials-api/financial/entity/${id}/resyncIntegration`,
    )?.catch((error) => null) as Promise<{ data: unknown }>;
  }
}
